import { useEffect, useMemo, useState } from 'react';
import type { ManagedOrganizationMember } from '@wx-weiqi/contracts';

import type { TeachingStore } from '../stores/teaching-store';
import { TeacherGamePickerDialog } from './TeacherGamePickerDialog';

const STUDENTS_PER_PAGE = 10;

type StudentDialog =
  | Readonly<{ kind: 'CREATE_CLASS' }>
  | Readonly<{ kind: 'CREATE_STUDENT' }>
  | Readonly<{ kind: 'EDIT_PROFILE'; student: ManagedOrganizationMember }>
  | Readonly<{ kind: 'RESET_PASSWORD'; student: ManagedOrganizationMember }>
  | Readonly<{ kind: 'ADJUST_CLASSES'; student: ManagedOrganizationMember }>
  | Readonly<{ kind: 'VIEW_GAMES'; student: ManagedOrganizationMember }>;

export interface StudentInformationPage {
  readonly students: readonly ManagedOrganizationMember[];
  readonly total: number;
  readonly page: number;
  readonly pageCount: number;
}

export function studentClassIds(
  student: ManagedOrganizationMember,
  state: ReturnType<TeachingStore['snapshot']>,
): readonly string[] {
  return state.classes
    .filter((managedClass) =>
      (state.membersByClass[managedClass.classId] ?? []).some(
        (member) => member.membership.membershipId === student.membershipId,
      ),
    )
    .map((managedClass) => managedClass.classId);
}

export function buildStudentInformationPage(
  state: ReturnType<TeachingStore['snapshot']>,
  classId: string,
  keyword: string,
  requestedPage: number,
): StudentInformationPage {
  const normalizedKeyword = keyword.trim().toLocaleLowerCase('zh-CN');
  const classMembershipIds =
    classId === ''
      ? null
      : new Set(
          (state.membersByClass[classId] ?? []).map((member) => member.membership.membershipId),
        );
  const filtered = state.students.filter((student) => {
    if (classMembershipIds !== null && !classMembershipIds.has(student.membershipId)) return false;
    if (normalizedKeyword.length === 0) return true;
    return [student.accountUsername ?? '', student.user.displayName].some((value) =>
      value.toLocaleLowerCase('zh-CN').includes(normalizedKeyword),
    );
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / STUDENTS_PER_PAGE));
  const page = Math.min(Math.max(1, requestedPage), pageCount);
  return Object.freeze({
    students: filtered.slice((page - 1) * STUDENTS_PER_PAGE, page * STUDENTS_PER_PAGE),
    total: filtered.length,
    page,
    pageCount,
  });
}

function studentClassNames(
  student: ManagedOrganizationMember,
  state: ReturnType<TeachingStore['snapshot']>,
): string {
  const names = studentClassIds(student, state)
    .map((classId) => state.classes.find((managedClass) => managedClass.classId === classId)?.name)
    .filter((name): name is string => name !== undefined);
  return names.length === 0 ? '未分班' : names.join(' / ');
}

export function StudentInformationPanel({
  teaching,
  state,
  onOpenReview,
}: {
  readonly teaching: TeachingStore;
  readonly state: ReturnType<TeachingStore['snapshot']>;
  readonly onOpenReview: (gameId: string, student: ManagedOrganizationMember) => void;
}) {
  const busy = state.pending !== null;
  const [draftClassId, setDraftClassId] = useState('');
  const [draftKeyword, setDraftKeyword] = useState('');
  const [classId, setClassId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<StudentDialog | null>(null);
  const [newClassName, setNewClassName] = useState('');
  const [newClassTerm, setNewClassTerm] = useState('');
  const [studentUsername, setStudentUsername] = useState('');
  const [studentDisplayName, setStudentDisplayName] = useState('');
  const [studentPassword, setStudentPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedClassIds, setSelectedClassIds] = useState<readonly string[]>([]);
  const result = useMemo(
    () => buildStudentInformationPage(state, classId, keyword, page),
    [classId, keyword, page, state],
  );

  useEffect(() => {
    if (result.page !== page) setPage(result.page);
  }, [page, result.page]);

  const closeDialog = () => {
    if (!busy) setDialog(null);
  };
  const openStudentDialog = (
    kind: 'EDIT_PROFILE' | 'RESET_PASSWORD' | 'ADJUST_CLASSES',
    student: ManagedOrganizationMember,
  ) => {
    setStudentDisplayName(student.user.displayName);
    setStudentPassword('');
    setConfirmPassword('');
    setSelectedClassIds(studentClassIds(student, state));
    setDialog({ kind, student });
  };
  const applyQuery = () => {
    setClassId(draftClassId);
    setKeyword(draftKeyword);
    setPage(1);
  };
  const openStudentGames = (student: ManagedOrganizationMember) => {
    teaching.clearError();
    setDialog({ kind: 'VIEW_GAMES', student });
    void teaching
      .loadTeacherGames({ studentMembershipId: student.membershipId })
      .catch(() => undefined);
  };

  return (
    <div className="student-information">
      <section className="student-information__toolbar" aria-label="学生查询与创建">
        <label>
          班级
          <select value={draftClassId} onChange={(event) => setDraftClassId(event.target.value)}>
            <option value="">全部班级</option>
            {state.classes.map((managedClass) => (
              <option key={managedClass.classId} value={managedClass.classId}>
                {managedClass.name}
              </option>
            ))}
          </select>
        </label>
        <label className="student-information__search">
          账号或姓名
          <input
            value={draftKeyword}
            placeholder="请输入账号 / 姓名"
            onChange={(event) => setDraftKeyword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyQuery();
            }}
          />
        </label>
        <button
          className="student-information__rect-button student-information__rect-button--primary"
          type="button"
          disabled={busy}
          onClick={applyQuery}
        >
          查询
        </button>
        <span className="student-information__toolbar-spacer" />
        <button
          className="student-information__rect-button"
          type="button"
          disabled={busy}
          onClick={() => setDialog({ kind: 'CREATE_CLASS' })}
        >
          创建班级
        </button>
        <button
          className="student-information__rect-button"
          type="button"
          disabled={busy}
          onClick={() => setDialog({ kind: 'CREATE_STUDENT' })}
        >
          创建学生
        </button>
      </section>

      <section className="student-information__directory">
        <header className="student-information__summary">
          <div>
            <h3>学生名单</h3>
            <p>
              共 {result.total} 名学生
              {classId !== '' || keyword !== '' ? ' · 当前为筛选结果' : ''}
            </p>
          </div>
          <button
            className="text-action"
            type="button"
            disabled={busy}
            onClick={() => void teaching.loadTeacherDashboard().catch(() => undefined)}
          >
            刷新
          </button>
        </header>
        <div className="student-information__table-wrap">
          <table className="student-information__table">
            <thead>
              <tr>
                <th>账号</th>
                <th>姓名</th>
                <th>所在班级</th>
                <th>学生操作</th>
              </tr>
            </thead>
            <tbody>
              {result.students.map((student) => (
                <tr key={student.membershipId}>
                  <td data-label="账号">
                    <strong>{student.accountUsername ?? '微信账号'}</strong>
                    {student.status === 'INACTIVE' ? <small>已停用</small> : null}
                  </td>
                  <td data-label="姓名">{student.user.displayName}</td>
                  <td data-label="所在班级">{studentClassNames(student, state)}</td>
                  <td data-label="学生操作">
                    <div className="student-information__row-actions">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openStudentDialog('EDIT_PROFILE', student)}
                      >
                        修改信息
                      </button>
                      <button
                        type="button"
                        disabled={busy || student.accountUsername === null}
                        title={
                          student.accountUsername === null ? '微信账号暂不支持教师重置密码' : ''
                        }
                        onClick={() => openStudentDialog('RESET_PASSWORD', student)}
                      >
                        修改密码
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openStudentDialog('ADJUST_CLASSES', student)}
                      >
                        调整班级
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openStudentGames(student)}
                      >
                        查看棋谱
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.students.length === 0 && !busy ? (
            <div className="student-information__empty">
              <strong>没有找到学生</strong>
              <span>请调整班级或账号、姓名查询条件。</span>
            </div>
          ) : null}
        </div>
        <nav className="student-information__pagination" aria-label="学生列表分页">
          <button
            type="button"
            disabled={result.page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            上一页
          </button>
          <span>
            第 {result.page} / {result.pageCount} 页
          </span>
          <button
            type="button"
            disabled={result.page >= result.pageCount}
            onClick={() => setPage((current) => current + 1)}
          >
            下一页
          </button>
        </nav>
      </section>

      {dialog?.kind === 'VIEW_GAMES' ? (
        <TeacherGamePickerDialog
          games={state.teacherGames}
          currentGameId={null}
          title={`${dialog.student.user.displayName}（${dialog.student.accountUsername ?? '微信账号'}）的棋谱`}
          scope="STUDENT"
          pending={state.pending === 'load-teacher-games' || state.pending === 'delete-game-record'}
          error={state.error}
          onClose={closeDialog}
          onRefresh={() => {
            void teaching
              .loadTeacherGames({ studentMembershipId: dialog.student.membershipId })
              .catch(() => undefined);
          }}
          onSelect={(gameId) => onOpenReview(gameId, dialog.student)}
          onDelete={(game) => {
            void teaching.deleteGameRecord(game).catch(() => undefined);
          }}
        />
      ) : dialog !== null ? (
        <div
          className="management-subdialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDialog();
          }}
        >
          <StudentInformationDialog
            dialog={dialog}
            state={state}
            teaching={teaching}
            busy={busy}
            newClassName={newClassName}
            newClassTerm={newClassTerm}
            studentUsername={studentUsername}
            studentDisplayName={studentDisplayName}
            studentPassword={studentPassword}
            confirmPassword={confirmPassword}
            selectedClassIds={selectedClassIds}
            onNewClassName={setNewClassName}
            onNewClassTerm={setNewClassTerm}
            onStudentUsername={setStudentUsername}
            onStudentDisplayName={setStudentDisplayName}
            onStudentPassword={setStudentPassword}
            onConfirmPassword={setConfirmPassword}
            onSelectedClassIds={setSelectedClassIds}
            onClose={closeDialog}
          />
        </div>
      ) : null}
    </div>
  );
}

function dialogTitle(dialog: StudentDialog): string {
  switch (dialog.kind) {
    case 'CREATE_CLASS':
      return '创建班级';
    case 'CREATE_STUDENT':
      return '创建学生';
    case 'EDIT_PROFILE':
      return '修改学生信息';
    case 'RESET_PASSWORD':
      return '修改学生密码';
    case 'ADJUST_CLASSES':
      return '调整学生班级';
    case 'VIEW_GAMES':
      return '查看学生棋谱';
  }
}

function StudentInformationDialog({
  dialog,
  state,
  teaching,
  busy,
  newClassName,
  newClassTerm,
  studentUsername,
  studentDisplayName,
  studentPassword,
  confirmPassword,
  selectedClassIds,
  onNewClassName,
  onNewClassTerm,
  onStudentUsername,
  onStudentDisplayName,
  onStudentPassword,
  onConfirmPassword,
  onSelectedClassIds,
  onClose,
}: {
  readonly dialog: StudentDialog;
  readonly state: ReturnType<TeachingStore['snapshot']>;
  readonly teaching: TeachingStore;
  readonly busy: boolean;
  readonly newClassName: string;
  readonly newClassTerm: string;
  readonly studentUsername: string;
  readonly studentDisplayName: string;
  readonly studentPassword: string;
  readonly confirmPassword: string;
  readonly selectedClassIds: readonly string[];
  readonly onNewClassName: (value: string) => void;
  readonly onNewClassTerm: (value: string) => void;
  readonly onStudentUsername: (value: string) => void;
  readonly onStudentDisplayName: (value: string) => void;
  readonly onStudentPassword: (value: string) => void;
  readonly onConfirmPassword: (value: string) => void;
  readonly onSelectedClassIds: (value: readonly string[]) => void;
  readonly onClose: () => void;
}) {
  const student = 'student' in dialog ? dialog.student : null;
  const passwordMatches = studentPassword === confirmPassword;
  const submit = (operation: Promise<void>, cleanup?: () => void) => {
    void operation
      .then(() => {
        cleanup?.();
        onClose();
      })
      .catch(() => undefined);
  };
  return (
    <section
      className={`management-subdialog-card${dialog.kind === 'CREATE_CLASS' ? ' management-subdialog-card--class' : ''}${dialog.kind === 'ADJUST_CLASSES' ? ' management-subdialog-card--class-adjustment' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={dialogTitle(dialog)}
    >
      <header className="lobby-dialog-heading">
        <div>
          <p className="eyebrow">学生信息</p>
          <h3>{dialogTitle(dialog)}</h3>
        </div>
        <button
          className="dialog-close"
          type="button"
          aria-label={`关闭${dialogTitle(dialog)}窗口`}
          disabled={busy}
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {dialog.kind === 'CREATE_CLASS' ? (
        <>
          <div className="form-grid form-grid--two">
            <label>
              班级名称
              <input
                value={newClassName}
                onChange={(event) => onNewClassName(event.target.value)}
                autoFocus
              />
            </label>
            <label>
              学期（选填）
              <input
                value={newClassTerm}
                onChange={(event) => onNewClassTerm(event.target.value)}
              />
            </label>
          </div>
          <button
            className="primary-button primary-button--wide"
            type="button"
            disabled={busy || newClassName.trim().length === 0}
            onClick={() =>
              submit(teaching.createClass(newClassName, newClassTerm), () => {
                onNewClassName('');
                onNewClassTerm('');
              })
            }
          >
            {state.pending === 'create-class' ? '正在创建…' : '创建班级'}
          </button>
          {state.classes.length > 0 ? (
            <div className="student-information__class-list">
              <h4>已有班级</h4>
              {state.classes.map((managedClass) => (
                <div key={managedClass.classId}>
                  <span>
                    <strong title={managedClass.name}>{managedClass.name}</strong>
                    <small>
                      {managedClass.termLabel ?? '未设置学期'} · {managedClass.activeStudentCount}{' '}
                      名学生
                    </small>
                  </span>
                  <button
                    className="danger-link"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (
                        window.confirm(`确定删除班级“${managedClass.name}”吗？历史对局会继续保留。`)
                      ) {
                        void teaching.archiveClass(managedClass).catch(() => undefined);
                      }
                    }}
                  >
                    删除班级
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {dialog.kind === 'CREATE_STUDENT' ? (
        <>
          <div className="form-grid form-grid--two">
            <label>
              登录账号
              <input
                value={studentUsername}
                onChange={(event) => onStudentUsername(event.target.value)}
                autoFocus
              />
            </label>
            <label>
              学生姓名
              <input
                value={studentDisplayName}
                onChange={(event) => onStudentDisplayName(event.target.value)}
              />
            </label>
          </div>
          <label>
            初始密码
            <input
              type="password"
              value={studentPassword}
              minLength={6}
              onChange={(event) => onStudentPassword(event.target.value)}
            />
          </label>
          <button
            className="primary-button primary-button--wide"
            type="button"
            disabled={
              busy ||
              studentUsername.trim().length < 3 ||
              studentDisplayName.trim().length === 0 ||
              studentPassword.length < 6
            }
            onClick={() =>
              submit(
                teaching.createStudent({
                  username: studentUsername,
                  displayName: studentDisplayName,
                  password: studentPassword,
                }),
                () => {
                  onStudentUsername('');
                  onStudentDisplayName('');
                  onStudentPassword('');
                },
              )
            }
          >
            {state.pending === 'create-student' ? '正在创建…' : '创建学生'}
          </button>
        </>
      ) : null}

      {dialog.kind === 'EDIT_PROFILE' && student !== null ? (
        <>
          <label>
            登录账号
            <input value={student.accountUsername ?? '微信账号'} disabled />
          </label>
          <label>
            学生姓名
            <input
              value={studentDisplayName}
              onChange={(event) => onStudentDisplayName(event.target.value)}
              autoFocus
            />
          </label>
          <div className="student-information__dialog-actions">
            <button
              className={student.status === 'ACTIVE' ? 'danger-action' : 'secondary-button'}
              type="button"
              disabled={busy}
              onClick={() => {
                const nextActive = student.status !== 'ACTIVE';
                if (
                  nextActive ||
                  window.confirm(`确定停用学生账号“${student.user.displayName}”吗？`)
                ) {
                  submit(teaching.setStudentActive(student, nextActive));
                }
              }}
            >
              {student.status === 'ACTIVE' ? '停用账号' : '恢复账号'}
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={
                busy ||
                studentDisplayName.trim().length === 0 ||
                studentDisplayName.trim() === student.user.displayName
              }
              onClick={() => submit(teaching.updateStudentProfile(student, studentDisplayName))}
            >
              保存信息
            </button>
          </div>
        </>
      ) : null}

      {dialog.kind === 'RESET_PASSWORD' && student !== null ? (
        <>
          <p className="student-information__dialog-note">
            正在为 <strong>{student.user.displayName}</strong>（{student.accountUsername}
            ）设置新密码。
          </p>
          <label>
            新密码
            <input
              type="password"
              minLength={6}
              value={studentPassword}
              onChange={(event) => onStudentPassword(event.target.value)}
              autoFocus
            />
          </label>
          <label>
            再次输入新密码
            <input
              type="password"
              minLength={6}
              value={confirmPassword}
              onChange={(event) => onConfirmPassword(event.target.value)}
            />
          </label>
          {!passwordMatches && confirmPassword.length > 0 ? (
            <p className="message message--error">两次输入的密码不一致。</p>
          ) : null}
          <button
            className="primary-button primary-button--wide"
            type="button"
            disabled={busy || studentPassword.length < 6 || !passwordMatches}
            onClick={() =>
              submit(teaching.resetStudentPassword(student, studentPassword), () => {
                onStudentPassword('');
                onConfirmPassword('');
              })
            }
          >
            {state.pending?.startsWith('student-password-') ? '正在保存…' : '确认修改密码'}
          </button>
        </>
      ) : null}

      {dialog.kind === 'ADJUST_CLASSES' && student !== null ? (
        <>
          <p className="student-information__dialog-note">
            为 <strong>{student.user.displayName}</strong> 选择所在班级，可同时加入多个班级。
          </p>
          <div className="student-information__class-options">
            {state.classes.map((managedClass) => (
              <label key={managedClass.classId}>
                <input
                  type="checkbox"
                  checked={selectedClassIds.includes(managedClass.classId)}
                  onChange={(event) =>
                    onSelectedClassIds(
                      event.target.checked
                        ? [...selectedClassIds, managedClass.classId]
                        : selectedClassIds.filter((classId) => classId !== managedClass.classId),
                    )
                  }
                />
                <span>
                  <strong title={managedClass.name}>{managedClass.name}</strong>
                  <small>{managedClass.termLabel ?? '未设置学期'}</small>
                </span>
              </label>
            ))}
            {state.classes.length === 0 ? <p className="history-empty">请先创建班级。</p> : null}
          </div>
          <button
            className="primary-button primary-button--wide"
            type="button"
            disabled={busy}
            onClick={() => submit(teaching.setStudentClasses(student, selectedClassIds))}
          >
            {state.pending?.startsWith('student-classes-') ? '正在调整…' : '保存班级调整'}
          </button>
        </>
      ) : null}
    </section>
  );
}
